import { ALL_DATES, dateFilterSummary } from '@utils/cbDateSettings'
import { splitMultiValue } from '@utils/reportFilterWire'

/**
 * "What is this filter set to?", in one short human string — spec 092.
 *
 * Lifted verbatim out of ReportView's applied-pill memo so the FILTER PILL's
 * trigger and the applied-criteria chip cannot disagree about the same value.
 * They did disagree once before, in a way nobody noticed for a while: CC-50009
 * had the panel showing the readable column twin ("Responsible Attorney") while
 * the chip showed the abbreviated filterable one ("RespAtty") — and because the
 * export header is built from those chips, the same mismatch shipped inside every
 * client CSV, Excel and PDF. One derivation, used by both, is the fix that keeps.
 *
 * Behaviour-preserving by construction: this is the same chain of type checks, in
 * the same order, with the same truncations.
 */

/** Does this value represent an actual constraint, or "no filter"? */
export const hasCriterion = (value) =>
  !(value == null || value === '' || value === ALL_DATES || value === 'ALL')

/**
 * @param {object} col   the report's column definition
 * @param {*}      value the current filter value
 * @returns {string} short display text, or '' when there is nothing to show
 */
export const filterValueSummary = (col, value) => {
  // CC-53890: a column whose EMPTY value is a real state rather than "no filter"
  // names it. On the General Ledger's account picker '' is "None selected",
  // which refuses to run — the pill must not read "Any" over a refusal.
  if (value === '' && col?.noneLabel) return col.noneLabel
  if (!hasCriterion(value)) return ''
  const type = col?.filterTypeName

  if (type === 'DATE' || type === 'SIMPLEDATE') return dateFilterSummary(value)
  if (type === 'BOOL') return value === '1' ? 'Yes' : 'No'

  // CC-53890: account ids are not something a reader can use ("115|128" is what
  // this used to fall through to), and the names load asynchronously inside the
  // control, so the pill states the COUNT. The control's own trigger names them.
  // A key the DEFINITION declares (a report's own "All" condition, CC-49867)
  // still reads by its declared label.
  if (type === 'ACCOUNTSDROPDOWN') {
    const keys = splitMultiValue(value)
    const declared = keys.map((k) => col.filterConditions?.find((c) => String(c.key) === k)?.value)
    if (declared.length && declared.every(Boolean)) return declared.join(', ')
    return `${keys.length} account${keys.length === 1 ? '' : 's'}`
  }

  if (type === 'DROPDOWN' || type === 'RADIO' || type === 'CHECKBOX') {
    // splitMultiValue: CB defaults arrive comma-separated (CC-49897).
    return splitMultiValue(value)
      .map((k) => col.filterConditions?.find((c) => String(c.key) === k)?.value || k)
      .join(', ')
  }

  if (type === 'LOOKUP' || type === 'TAGMANAGER') {
    // CC-49866: values carry "id:name" — show the names ("Aaby Daniels"),
    // falling back to a count for ids-only values (older persisted filters).
    const parts = String(value).split('|').filter(Boolean)
    const names = parts
      .map((p) => (p.includes(':') ? p.slice(p.indexOf(':') + 1) : ''))
      .filter(Boolean)
    return names.length === parts.length && names.length > 0
      ? names.join(', ').slice(0, 60)
      : `${parts.length} selected`
  }

  return String(value).slice(0, 40)
}

/**
 * Whether a criterion can be removed by dismissing its chip.
 *
 * RADIO, BOOL and SIMPLEDATE always carry exactly one value (CC-49861 / CC-49925)
 * — "removing" one would leave an empty value the wire simply omits, so the
 * stored proc would run UNSCOPED while the chip disappeared as though something
 * had been narrowed. An absent filter widens on this backend; it is never neutral.
 */
export const isRemovableFilter = (col) =>
  col?.filterTypeName !== 'SIMPLEDATE'
  && col?.filterTypeName !== 'RADIO'
  && col?.filterTypeName !== 'BOOL'
